import {Application} from 'src';
import {IMicroApp} from './IMicroApp';
import {AnyObject} from 'src';
import {banEffect, effect} from 'src/effect/effect';
import {reactive} from 'src/effect/reactive';
import {readonly} from 'src/effect/readonly';
import {queueJob} from 'src/effect/scheduler';
import bootstrapApp from 'src/lifecycle/bootstrap';
import mountApp from 'src/lifecycle/mount';
import {deepCopy} from 'src/utils/deepCopy';
import {isNull} from 'src/utils/utils';

/**
 * 模块挂载
 */
export default class MicroApp implements IMicroApp {
  private appMaps = new Map<string, Application>();

  /**
   * 响应性注册函数
   */
  private effectFnMaps = new Map<string, Function>();

  private static _instance: MicroApp | undefined;

  private options = reactive({});

  private constructor() {}

  //主app 最后一个引导 保证项目参数加载完毕后 启动
  public static HOST_APP = '__MICRO_HOST_APP__';

  //单例
  static instance(): MicroApp {
    if (this._instance === undefined) {
      this._instance = new MicroApp();
    }
    return this._instance!!;
  }

  /**
   * 宿主模块给子项目传参
   * @param options
   */
  props(options: AnyObject) {
    if (options === null || undefined) return;
    // this.options = { ...options }; 对象重组 不是操作原地址
    //深度拷贝
    deepCopy(options, this.options);
  }

  /**
   * 注册模块
   * @param app
   */
  registerApplication(app: Application) {
    if (app === null || undefined) return;

    if (this.appMaps.has(app.name)) {
      throw new Error(`registerApplication(${app.name}) is exist`);
    }

    //未挂载
    app.isMounted = false;

    this.appMaps.set(app.name, app);
  }

  /**
   * 主项目启动
   */
  async start() {
    const result = this.getUnMountApp();

    const mountPromise = result.map(app => {
      //mountApp option 改变触发 update
      mountApp(app, this.options);
    });

    await mountPromise;

    const bootstrapPromise = result.map(app => {
      bootstrapApp(app);
    });

    await bootstrapPromise;

    result.forEach((app, index) => {
      app.isMounted = true;
    });

    console.log(`${MicroApp.HOST_APP} is ready`);

    //设置响应性
    result.forEach(app => {
      let effectFn = effect(
        () => {
          //update 传入属性是只读的 内部修改options 不会触发update 禁止 调用 prop()传参设置值，否则循环引用
          app.update(readonly(this.options));
        },
        {scheduler: queueJob},
      );
      //注册响应性方法 避免内存泄漏
      this.effectFnMaps.set(app.name, effectFn);
    });
  }

  async unregister(appName: string) {
    const app = this.appMaps.get(appName);
    if (!isNull(app)) {
      //模块卸载 不会导致更新 必须通过发事件方式处理
      await app?.unmount(readonly(this.options));
      let effectFn = this.effectFnMaps.get(appName);
      banEffect(effectFn!!);
      this.effectFnMaps.delete(appName);
      this.appMaps.delete(appName);
    }
  }

  /**
   * 获得未挂载app
   * @returns
   */
  private getUnMountApp(): Application[] {
    const result: Application[] = [];

    let hostapp = null;
    this.appMaps.forEach((app, key) => {
      if (!app.isMounted) {
        //未挂载的app
        if (app.name == MicroApp.HOST_APP) {
          hostapp = app;
        } else {
          result.push(app);
        }
      }
    });

    //hostapp 最后一个引导启动
    if (hostapp) {
      result.push(hostapp);
    }
    return result;
  }

  /**
   * 获得挂载app
   * @returns
   */
  private getMountedApp(): Application[] {
    const result: Application[] = [];

    this.appMaps.forEach((app, key) => {
      if (app.isMounted) {
        result.push(app);
      }
    });
    return result;
  }
}

/**
 * 注册应用
 * @param app
 */
export function registerApplication(app: Application) {
  MicroApp.instance().registerApplication(app);
}

/**
 * 解除模块注册
 * @param appName
 */
export function unregister(appName: string) {
  MicroApp.instance().unregister(appName);
}

/**
 * 宿主传参
 * @param options
 */
export function props(options: AnyObject) {
  MicroApp.instance().props(options);
}

/**
 * 项目启动
 */
export function start() {
  MicroApp.instance().start();
}
